import React, { useMemo } from 'react';
import * as THREE from 'three';
import {
  CELL_SIZE,
  INVALID_NFT_PLACEMENT_COLOR,
  NFT_SELECTION_COLOR,
} from '../constants';
import { useNFTContext } from '../context/NFTContext';
import { getWorldPositionFromTile } from '../utils/floor_utils';

const RoomPlacementHighlightBox = () => {
  const {
    nftPlacingCells,
    nftPlacingModel,
    isNFTPlacingMode,
    isNFTPlacingValid,
  } = useNFTContext();

  const selectionEdgeColor = useMemo<number>(() => {
    if (isNFTPlacingValid) {
      return NFT_SELECTION_COLOR; // white
      // return VALID_NFT_PLACEMENT_COLOR; // green color when valid
    } else {
      return INVALID_NFT_PLACEMENT_COLOR; // red
    }
  }, [isNFTPlacingValid]);

  const nftPlacingHighlightInfo = useMemo<{
    position: THREE.Vector3;
    scale: THREE.Vector3;
  }>(() => {
    if (nftPlacingCells.length > 0 && nftPlacingModel) {
      const amountOfBoxesX = nftPlacingModel.widthX;
      const amountOfBoxesZ = nftPlacingModel.heightZ;
      const worldPosition = getWorldPositionFromTile(
        nftPlacingCells[0],
        CELL_SIZE,
        amountOfBoxesX,
        amountOfBoxesZ
      );

      return {
        position: new THREE.Vector3(worldPosition.x, 0.1, worldPosition.z),
        scale: new THREE.Vector3(amountOfBoxesZ, 0.98, amountOfBoxesX),
      };
    } else {
      return {
        position: new THREE.Vector3(
          Number.MAX_SAFE_INTEGER,
          Number.MAX_SAFE_INTEGER,
          Number.MAX_SAFE_INTEGER
        ),
        scale: new THREE.Vector3(1, 1, 1),
      };
    }
  }, [nftPlacingCells, nftPlacingModel]);

  if (isNFTPlacingMode && nftPlacingModel)
    return (
      <>
        {/* outter top line */}
        <mesh
          position={[
            nftPlacingHighlightInfo.position.x +
              (nftPlacingModel?.heightZ * CELL_SIZE) / 2 -
              1.5,
            nftPlacingHighlightInfo.position.y + 0.27,
            nftPlacingHighlightInfo.position.z,
          ]}
          scale={
            new THREE.Vector3(
              0.1,
              nftPlacingHighlightInfo.scale.y,
              nftPlacingHighlightInfo.scale.z
            )
          }
          renderOrder={150}
        >
          <boxGeometry args={[CELL_SIZE * 0.99, CELL_SIZE, CELL_SIZE * 0.99]} />
          <meshStandardMaterial
            depthWrite={false}
            transparent={true}
            opacity={1}
            color={selectionEdgeColor}
          />
        </mesh>
        {/* outter bottom line */}
        <mesh
          position={[
            nftPlacingHighlightInfo.position.x -
              (nftPlacingModel?.heightZ * CELL_SIZE) / 2 +
              1.48,
            nftPlacingHighlightInfo.position.y + 0.27,
            nftPlacingHighlightInfo.position.z,
          ]}
          scale={
            new THREE.Vector3(
              0.1,
              nftPlacingHighlightInfo.scale.y,
              nftPlacingHighlightInfo.scale.z
            )
          }
          renderOrder={150}
        >
          <boxGeometry args={[CELL_SIZE * 0.99, CELL_SIZE, CELL_SIZE * 0.99]} />
          <meshStandardMaterial
            depthWrite={false}
            transparent={true}
            opacity={1}
            color={selectionEdgeColor}
          />
        </mesh>
        {/* outter left line */}
        <mesh
          position={[
            nftPlacingHighlightInfo.position.x - 0.01,
            nftPlacingHighlightInfo.position.y + 0.27,
            nftPlacingHighlightInfo.position.z -
              (nftPlacingModel?.widthX * CELL_SIZE) / 2 +
              1.5,
          ]}
          scale={
            new THREE.Vector3(
              nftPlacingHighlightInfo.scale.x,
              nftPlacingHighlightInfo.scale.y,
              0.1
            )
          }
          renderOrder={150}
        >
          <boxGeometry args={[CELL_SIZE * 0.99, CELL_SIZE, CELL_SIZE * 0.99]} />
          <meshStandardMaterial
            depthWrite={false}
            transparent={true}
            opacity={1}
            color={selectionEdgeColor}
          />
        </mesh>
        {/* outter right line */}
        <mesh
          position={[
            nftPlacingHighlightInfo.position.x,
            nftPlacingHighlightInfo.position.y + 0.27,
            nftPlacingHighlightInfo.position.z +
              (nftPlacingModel?.widthX * CELL_SIZE) / 2 -
              1.5,
          ]}
          scale={
            new THREE.Vector3(
              nftPlacingHighlightInfo.scale.x,
              nftPlacingHighlightInfo.scale.y,
              0.1
            )
          }
          renderOrder={150}
        >
          <boxGeometry args={[CELL_SIZE * 0.99, CELL_SIZE, CELL_SIZE * 0.99]} />
          <meshStandardMaterial
            depthWrite={false}
            transparent={true}
            opacity={1}
            color={selectionEdgeColor}
          />
        </mesh>
        {/* inner */}
        <mesh
          position={[
            nftPlacingHighlightInfo.position.x,
            nftPlacingHighlightInfo.position.y + 0.5,
            nftPlacingHighlightInfo.position.z,
          ]}
          scale={nftPlacingHighlightInfo.scale}
          renderOrder={150}
        >
          <boxGeometry args={[CELL_SIZE * 0.99, CELL_SIZE, CELL_SIZE * 0.99]} />
          <meshStandardMaterial
            depthWrite={false}
            transparent={true}
            opacity={0.49}
            color={selectionEdgeColor}
          />
        </mesh>
      </>
    );

  return null;
};

export default RoomPlacementHighlightBox;
